import { useEffect } from 'react'

import { ROUTING_OVERRIDE_KEY, resolveRoutingSettings } from '~/portal/hooks/useRoutingSettings'
import { RoutingOption, RoutingSettings } from '~/shared/types/portal.ts'
import { UserInfo } from '~/shared/types/user.ts'

export const createDeepLinkPath = (str: string) => str.trim().replace(/\s/g, '%20')

export const escapeDeepLinkPath = (str: string) => CSS.escape(createDeepLinkPath(str).replace(/%20/g, '_'))

export const useSwaggerScroll = (ref: HTMLDivElement | undefined, hash: string) => {

  const scrollToElement = (element: HTMLElement, isSchema: boolean) => {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' })

    if (isSchema) {
      const button = element.querySelector('button.model-box-control')
      if (button?.getAttribute('aria-expanded') === 'false') {
        button.dispatchEvent(new MouseEvent('click', { bubbles: true }))
      } else if (!button) {
        // OAS 3.1 case
        const accordionButton = element.querySelector('button.json-schema-2020-12-accordion')
        if (accordionButton) {
          accordionButton.dispatchEvent(new MouseEvent('click', { bubbles: true }))
        }
      }
    } else {
      const button = element.querySelector('.opblock-summary-control')
      if (button?.getAttribute('aria-expanded') === 'false') {
        button.dispatchEvent(new MouseEvent('click', { bubbles: true }))
      } 
    }
  }

  useEffect(() => {
    if (!ref || !hash) {
      return
    }

    const parts = hash.replace('#/', '').split('/')
    const isSchema = parts.length === 1
    const tagOrSchema = parts[0] || ''
    const operation = isSchema ? '' : parts[1] || ''

    // Expand collapsed sections immediately for SPA navigation (Swagger UI already rendered)
    const tagSection = ref.querySelector(`[data-tag="${tagOrSchema}"]`)
    if (tagSection?.getAttribute('data-is-open') === 'false') {
      tagSection.dispatchEvent(new MouseEvent('click', { bubbles: true }))
    }

    if (isSchema) {
      const schemasButton = ref.querySelector('button.models-control')
      if (schemasButton?.getAttribute('aria-expanded') === 'false') {
        schemasButton.dispatchEvent(new MouseEvent('click', { bubbles: true }))
      }
    }

    let timeoutId: NodeJS.Timeout
    let attempts = 0
    const MAX_ATTEMPTS = 20
    const RETRY_INTERVAL_MS = 150

    const tryScroll = () => {
      const escaped = isSchema
        ? `model-${tagOrSchema}`
        : escapeDeepLinkPath(['operations', tagOrSchema, operation].join('-'))

      let contentSection = document.getElementById(escaped)

      if (contentSection) {
        scrollToElement(contentSection, isSchema)
        return
      }

      if (isSchema) {
        // Find the content section by schema title
        const schemaTitle = decodeURIComponent(tagOrSchema)
        contentSection = Array.from(document.querySelectorAll('.model-container')).find((element) => {
          const titleElement = element.querySelector('.model-title')
          return titleElement?.textContent?.toLowerCase() === schemaTitle?.toLowerCase()
        }) as HTMLElement
        if (!contentSection) {
          // Find content section in OAS 3.1
          contentSection = Array.from(document.querySelectorAll('.json-schema-2020-12')).find((element) => {
            const titleElement = element.querySelector('.json-schema-2020-12__title')
            return titleElement?.textContent?.toLowerCase() === schemaTitle?.toLowerCase()
          }) as HTMLElement
        }
        if (contentSection) {
          scrollToElement(contentSection, isSchema)
          return
        }
      } else {
        // Swagger UI uses '_' instead of '-' in operation IDs built from operation paths.
        // If it can't find the operation, replace any '-' and retry scrolling.
        const escapedAlt = escapeDeepLinkPath(['operations', tagOrSchema, operation.replaceAll('-', '_')].join('-'))
        const altSection = document.getElementById(escapedAlt)
        if (altSection) {
          scrollToElement(altSection, isSchema)
          return
        }
      }

      if (attempts < MAX_ATTEMPTS) {
        attempts++
        timeoutId = setTimeout(tryScroll, RETRY_INTERVAL_MS)
      }
    }

    timeoutId = setTimeout(tryScroll, RETRY_INTERVAL_MS)
    return () => clearTimeout(timeoutId)
  }, [hash, ref])
}

export const getRequestInterceptor = (user?: UserInfo, forceProxy = false, portalSettings?: RoutingSettings) => (req) => {
  // read at request time so a routing change made in the modal applies without remounting swagger-ui
  const override = localStorage.getItem(ROUTING_OVERRIDE_KEY)
  const { routing, credentialsEnabled } = resolveRoutingSettings(override ? JSON.parse(override) : undefined, portalSettings)

  // the STS token must never reach the browser, so it can only be injected by the proxy
  if (!forceProxy && routing === RoutingOption.BROWSER) {
    if (credentialsEnabled) {
      req.credentials = 'include'
    }
    return req
  }

  req.url = `/services/api/proxy/${user?.proxyToken || ''}/${req.url}`

  return req
}